import { useState } from "react";
import {
  Button,
  Chip,
  Icon,
  List,
  Text,
  Accordion,
} from "@ui-component-library";
import { DynamicModal } from "@components/DynamicModal";
import { useSubscription } from "../../../context/SubscriptionCheckoutContext";
import { getTotalForestArea } from "../utils";
import { formatPrice } from "../../../utils";

const comparePackages = [
  {
    title: "Skogskarta",
    description:
      "En klickbar karta över den angivna fastighetsbeteckningen där du kan klicka på respektive avdelning",
    includedIn: ["bas", "plus"],
    key: "skogskarta",
  },
  {
    title: "Automatiska avdelningar",
    description:
      "Vi delar upp skogen i lämpliga avdelningar utifrån skogens beskaffenhet och rationella områden",
    includedIn: ["bas", "plus"],
    key: "automatiska-avdelningar",
  },
  {
    title: "Automatiskt uppdaterad plan",
    description: "Vi uppdaterar med den senaste tillgängliga informationen",
    includedIn: ["bas", "plus"],
    key: "automatiskt-uppdaterad-plan",
  },
  {
    title: "Data per avdelning",
    description:
      "All information som i en vanlig plan men också intäkt/kostnad per föreslagen åtgärd",
    includedIn: ["bas", "plus"],
    key: "data-per-avdelning",
  },
  {
    title: "Redigera skogsbruksplan",
    description: "Rita, radera och ändra",
    includedIn: ["plus"],
    key: "redigera-skogsbruksplan",
  },
  {
    title: "Skogsöverblick",
    description: "En sammanställning över fastigheten med fastighetsvärde m.m.",
    includedIn: ["plus"],
    key: "skogsoverblick",
  },
  {
    title: "Kartlager",
    description: "Alla relevanta kartlager samlade i en användarvänlig vy",
    includedIn: ["plus"],
    key: "kartlager",
  },
  {
    title: "Ladda upp Forestand-fil",
    description:
      "Möjlighet att ladda upp din tidigare skogsbruksplan för att exempelvis behålla avdelningsindelning",
    includedIn: ["plus"],
    key: "ladda-upp-forestand-fil",
  },
  {
    title: "Ladda ner skogsbruksplan i PDF",
    description: "Få din plan i ett utskriftsvänligt format",
    includedIn: ["plus"],
    key: "download-plan-pdf",
  },
  {
    title: "Lantmäteriets ortofoton",
    description: "Se historiska och detaljerade kartor över din fastighet",
    includedIn: ["plus"],
    key: "lantmateriets-ortofoton",
  },
  {
    title: "Placera markörer",
    description:
      "Gör dina egna markeringar i kartan för att komma ihåg och besöka",
    includedIn: ["plus"],
    key: "placera-markorer",
  },
  {
    title: "Anteckningar per avdelning",
    description:
      "Gör din egna anteckningar direkt i kartan för att komma ihåg och besöka",
    includedIn: ["plus"],
    key: "anteckningar-per-avdelning",
  },
  {
    title: "Volym",
    description:
      "Den senast tillgängliga informationen per avdelning och för fastigheten uppräknat till en aktuell volym",
    includedIn: ["plus"],
    key: "volym",
  },
  {
    title: "Areal",
    description:
      "Areal per avdelning och för fastigheten uppdelat på de viktigaste kategorierna",
    includedIn: ["plus"],
    key: "areal",
  },
  {
    title: "Medelbonitet",
    description:
      "Utifrån tillgänglig information kan vi skatta boniteten för fastigheten",
    includedIn: ["plus"],
    key: "medelbonitet",
  },
  {
    title: "Trädslagsfördelning",
    description:
      "Utixfrån tillgänglig information kan vi ange trädslag per avdelning och för fastigheten",
    includedIn: ["plus"],
    key: "trädslagsfördelning",
  },
  {
    title: "Åldersfördelning",
    description:
      "Vi fördelar åldern över både volym och areal för att du ska få bästa möjliga information om skogen",
    includedIn: ["plus"],
    key: "åldersfördelning",
  },
  {
    title: "Huggningklasser",
    description:
      "Vi analyserar skogen och föreslår huggningsklass per avdelning",
    includedIn: ["plus"],
    key: "huggningklasser",
  },
  {
    title: "Avdelningsbeskrivning",
    description:
      "Vi listar och anger nyckeldata för alla avdelningar på ett lättläst och överskådligt sätt",
    includedIn: ["plus"],
    key: "avdelningsbeskrivning",
  },
  {
    title: "Virkespriser",
    description:
      "Treebulas unika virkespris-statistik baseras på verkliga affärer och information från skogsbolagen",
    includedIn: ["plus"],
    key: "virkespriser",
  },
  {
    title: "Verkliga virkespriser",
    description:
      "Unik statistik som bygger på riktiga affärer gjorda på Treebulas marknadsplats för virke",
    includedIn: ["plus"],
    key: "verkliga-virkespriser",
  },
  {
    title: "Virkesvärde per avdelning",
    description:
      "Vi ger varje avdelning en aktuell värdering utifrån våra uppdaterade virkespriser",
    includedIn: ["plus"],
    key: "virkesvärde-per-avdelning",
  },
  {
    title: "Massavedspriser med bolagsnamn",
    description:
      "Uppdaterad information om vad olika skogsbolag betalar för massaved",
    includedIn: ["plus"],
    key: "massavedspriser-med-bolagsnamn",
  },
  {
    title: "Virkespriskollen",
    description:
      "Redan fått anbud? Vi hjälper dig jämföra och göra en bra affär",
    includedIn: ["plus"],
    key: "virkespriskollen",
  },
  {
    title: "Fastighetsvärdering",
    description:
      "En uppdaterad fastighetsvärdering baserad på senaste publika prisstatistiken och virkesförråd",
    includedIn: ["plus"],
    key: "estate-valuation",
  },
  {
    title: "Värdera din skog",
    description:
      "Uppdaterad fastighetsvärdering baserad på den senaste prisstatistiken över gjorda skogsfastighetsaffärer",
    includedIn: ["plus"],
    key: "värdera-din-skog",
  },
  {
    title: "Historisk prisutveckling på fastighet",
    description:
      "Historisk data som visar prisutvecklingen på skogsfastigheter i ditt område",
    includedIn: ["plus"],
    key: "historisk-prisutveckling-pa-fastighet",
  },
  {
    title: "Skogsskador",
    description:
      "Vi analyserar din skog med satellitteknik och visar var det finns skogsskador",
    includedIn: ["plus"],
    key: "skogsskador",
  },
  {
    title: "Granbarkborrskador",
    description:
      "Vi analyserar din skog med satellitteknik och visar var det kan finnas skador pga. barkborre",
    includedIn: ["plus"],
    key: "granbarkborrskador",
  },
  {
    title: "Vindanalys, stormskador & kantzoner i risk",
    description:
      "Vi analyserar historiskt väder med den senaste månaden och pekar ut risker",
    includedIn: ["plus"],
    key: "vindanalys-stormskador-kantzoner-i-risk",
  },
  {
    title: "Barkborrerisk",
    description: "Vi anger risknivå för barkborreangrepp",
    includedIn: ["plus"],
    key: "barkborrerisk",
  },
  {
    title: "Brandrisk",
    description: "Vi anger risknivå för brand",
    includedIn: ["plus"],
    key: "brandrisk",
  },
  {
    title: "Grundvattennivå",
    description: "Vi anger nivå i förhållande till \u201cnormal\u201d",
    includedIn: ["plus"],
    key: "grundvattenniva",
  },
  {
    title: "Observation av rödlistade arter",
    description:
      "Vi visar om och var det görs observationer på och i närheten av din fastighet",
    includedIn: ["plus"],
    key: "observation-av-rodlistade-arter",
  },
  {
    title: "Avverkningsanmälan i närheten",
    description:
      "Ska grannen avverka? Vi visar om och var det görs anmälan i närheten",
    includedIn: ["plus"],
    key: "avverkningsanmalan-i-narheten",
  },
  {
    title: "Vind",
    description:
      "Senaste statistiken över vindstyrka och riktning på din fastighet. Visar risknivå och utsatta kantzoner",
    includedIn: ["plus"],
    key: "vind",
  },
  {
    title: "Temperatur",
    description: "Senaste temperaturinformation över din fastighet",
    includedIn: ["plus"],
    key: "temperatur",
  },
  {
    title: "Nederbörd",
    description: "Senaste nederbördinformation över din fastighet",
    includedIn: ["plus"],
    key: "nederbord",
  },
  {
    title: "Blixtnedslag",
    description: "Visar om och var blixten slå ner",
    includedIn: ["plus"],
    key: "blixtnedslag",
  },
  {
    title: "Vindanalys",
    description:
      "Vi analyserar historiskt vind med den senaste månaden och pekar ut risker",
    includedIn: ["plus"],
    key: "vindanalys",
  },
  {
    title: "Vindros analys",
    description: "",
    includedIn: ["plus"],
    key: "vindros-analys",
  },
  {
    title: "Rekommendationer",
    description:
      "Vi hjälper dig med konkreta rekommendationer när skador upptäcks",
    includedIn: ["plus"],
    key: "rekommendationer",
  },
  {
    title: "Översikt 30-dagar",
    description:
      "Vi ger dig en uppdaterad bild av händelser och väder den senaste månaden",
    includedIn: ["plus"],
    key: "oversikt-30-dagar",
  },
  {
    title: "Högsta byvind",
    description:
      "Hur mycket har det blåst som mest på din fastighet och föranleder det risk för vindfällen",
    includedIn: ["plus"],
    key: "hogsta-byvind",
  },
  {
    title: "Genomsnittlig vindstyrka",
    description:
      "Hur mycket har det blåst på din fastighet och föranleder det risk för vindfällen",
    includedIn: ["plus"],
    key: "genomsnittlig-vindstyrka",
  },
  {
    title: "Marknadsbrev",
    description:
      "Håll dig uppdaterad med Treebulas marknadsbrev för att se hur bla. virkespriserna förändras där du har din skog",
    includedIn: ["plus"],
    key: "marknadsbrev",
  },
  {
    title: "Virkesprisindikatorn",
    description:
      "Treebulas unika Virkesprisindikator som berättar vart virkespriset befinner sig",
    includedIn: ["plus"],
    key: "virkesprisindikatorn",
  },
  {
    title: "Treebula studion",
    description:
      "Ta del av aktuell information och få koll på vad som händer på marknaden",
    includedIn: ["plus"],
    key: "treebula-studion",
  },
  {
    title: "Se potential för Co2",
    description:
      "Har du potential för att tjäna pengar på kolkrediter i din skog? Vi analyserar och visar potentialen",
    includedIn: ["plus"],
    key: "se-potential-for-co2",
  },
  {
    title: "Tjäna kolkrediter",
    description:
      "Vi hjälper dig vidare i processen att tjäna pengar på kolkrediter i din skog",
    includedIn: ["plus"],
    key: "tjana-kolkrediter",
  },
  {
    title: "Rådgivning",
    description:
      "Om du behöver stöd eller vägledning finns våra rådgivare alltid redo att hjälpa dig",
    includedIn: ["plus"],
    key: "radgivning",
  },
];

const FeatureCard = ({ title, description, imageUrl }) => {
  return (
    <div className="relative flex rounded-xl overflow-hidden">
      <img
        src={Window.static_path + imageUrl}
        alt={title}
        className="absolute w-full h-full object-cover"
      />
      <div className="absolute w-full h-full bg-gradient-to-b from-transparent from-0% via-charcoal-500/70 via-5% via-charcoal-500/80 via-60% to-charcoal-500 to-90%"></div>
      <div className="flex flex-col w-full h-full items-start z-10 p-4 gap-10">
        <Chip
          label="Plus"
          size="medium"
          className="bg-[#F6F5FE] text-[#6856C3] fill-[#6856C3]"
          icon="SparkleSimple"
        />
        <div className="">
          <Text
            variant="text-base/leading-normal/medium"
            className="text-white mb-2"
          >
            {title}
          </Text>
          <Text
            variant="text-sm/leading-normal/normal"
            className="text-[#ffffffd9]"
          >
            {description}
          </Text>
        </div>
      </div>
    </div>
  );
};

const columnLabels = {
  bas: "Bas",
  plus: "Plus",
  premium: "Premium",
};

export default function SubscriptionDetailsDialog({ isOpen, onClose, subscription }) {
  const { selectedProperties } = useSubscription();
  const [selectedComparePackage] = useState("plus");
  const totalForestArea = getTotalForestArea(selectedProperties);

  return (
    <DynamicModal
      isOpen={isOpen}
      onClose={onClose}
      bp={{ sm: "drawer", lg: "sheet" }}
    >
      <DynamicModal.Header>
        <>
          <Text
            variant="text-base/leading-normal/medium"
            className="text-charcoal-500"
          >
            Bli Pluskund hos Treebula
          </Text>
          <Button
            tw
            onClick={onClose}
            size="icon"
            variant="tertiary"
          >
            <Icon tw name="CrossSimple" size="large" />
          </Button>
        </>
      </DynamicModal.Header>
      <DynamicModal.Content>
        <>
          <div className="flex flex-col gap-4 ">
            <div className="flex flex-col gap-2">
              <Text
                variant="text-lg/leading-normal/medium"
                className="text-charcoal-500"
              >
                Treebula Plus
              </Text>
              {totalForestArea > 0 && (
                <div className="flex flex-row gap-1 items-baseline">
                  <Text
                    variant="text-sm/leading-normal/medium"
                    className="text-charcoal-500"
                  >
                    {formatPrice(subscription?.price)} kr/år
                  </Text>
                </div>
              )}

              <Text
                variant="text-sm/leading-normal/normal"
                className="text-charcoal-500"
              >
                Bli en smartare skogsägare och gör bättre affärer med Treebula
                Plus
              </Text>

              <Text
                variant="text-xs/leading-normal/normal"
                className="text-charcoal-500"
              >
                Med Treebula Plus får du <b>full kontroll</b> över din skog och{" "}
                <b>nya möjligheter</b> att skapa värde. Du får en{" "}
                <b>smart, trygg</b> och <b>aktuell</b> helhetslösning som
                hjälper dig att <b>maximera lönsamheten</b> och fatta{" "}
                <b>välgrundade beslut</b> för din fastighets framtid.
              </Text>

              <List
                tw
                items={[
                  "Komplett digital skogsbruksplan",
                  "Fullständig tillgång till prisjämförelse på virke",
                  "Skogsövervakning",
                  "Fastighetsvärdering",
                  "Marknadsinformation och notiser",
                ]}
              />
            </div>
            <div className="flex flex-col gap-6 "></div>
          </div>

          <div className="flex flex-col">
            <Text
              variant="text-base/leading-normal/medium"
              className="text-charcoal-500 mb-2"
            >
              Urval av funktioner du får tillgång till
            </Text>

            <div className="flex flex-col gap-4">
              <FeatureCard
                title="Uppdaterad skogsbruksplan"
                description="Utforska fastigheter med detaljerade topografiska kartor. Alltid den senaste informationen och värden uppräknade för aktuell data"
                imageUrl="img/subscriptions/kartoversikt.webp"
              />
              <FeatureCard
                title="Prisjämförelse för virke"
                description="Analysera fastigheter från ovan med högupplösa flygbilder. Treebulas unika prisdatabas säkerställer att du gör bra affärer. Vi håller din skog under ständig uppsikt med hjälp av satellitövervakning"
                imageUrl="img/subscriptions/timber_prices.webp"
              />
              <FeatureCard
                title="Storm- och skogsskador"
                description="Upptäck skador tidigt och förutse risker med AI-analys"
                imageUrl="img/subscriptions/stormskador.webp"
              />
              <FeatureCard
                title="Fastighetsvärdering"
                description="Vi värderar din fastighet baserat på den senast tillgängliga prisstatistiken och data om din skog"
                imageUrl="img/subscriptions/fastighetsvarderingar.webp"
              />

              <Text variant="text-sm/leading-normal/normal">
                Och mycket mer...
              </Text>

              <table className="w-full table-auto">
                <thead className="table-header-group bg-white-50">
                  <tr>
                    <Text
                      variant="text-xs/leading-normal/medium"
                      as="th"
                      className="text-left py-2 px-2 w-[40%] text-xs"
                    >
                      Det här får du
                    </Text>
                    <Text
                      variant="text-xs/leading-normal/medium"
                      as="th"
                      className="text-center py-2 px-1 w-[30%] text-xs"
                    >
                      {columnLabels["bas"]}
                    </Text>
                    <Text
                      variant="text-xs/leading-normal/medium"
                      as="th"
                      className="text-center py-2 px-1 w-[30%] text-[12px] bg-purple-plum-50 rounded-t-xl"
                    >
                      {columnLabels[selectedComparePackage]}
                    </Text>
                  </tr>
                </thead>

                <tbody>
                  {comparePackages.map((comparePackage) => (
                    <tr
                      key={comparePackage.key}
                      className="border-solid border-t-[0.5px] border-charcoal-500/16"
                    >
                      <td className="py-3 pr-2 lg:py-4">
                        <Accordion underline={false}>
                          <Accordion.Header>
                            <Text
                              variant="text-xs/leading-normal/normal"
                              className="text-charcoal-500"
                            >
                              {comparePackage.title}
                            </Text>
                          </Accordion.Header>
                          <Accordion.Content>
                            <Text
                              variant="text-xs/leading-normal/normal"
                              className="text-[#1A1A1A] block lg:hidden"
                            >
                              {comparePackage.description}
                            </Text>
                            <Text
                              variant="text-xs/leading-normal/normal"
                              className="text-[#171717] hidden lg:block"
                            >
                              {comparePackage.description}
                            </Text>
                          </Accordion.Content>
                        </Accordion>
                      </td>

                      {["bas", selectedComparePackage].map((key) => (
                        <td
                          key={key}
                          className={`text-center ${key !== "bas" ? "bg-purple-plum-50" : ""
                            }`}
                        >
                          {comparePackage.includedIn.includes(key) && (
                            <Icon
                              tw
                              size="medium"
                              name="CheckSimple"
                              className="fill-purple-plum-500"
                            />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      </DynamicModal.Content>
    </DynamicModal>
  );
}
